import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { t, Trans } from '@lingui/macro';
import { AddCircleOutline, FileCopyOutlined, Save } from '@material-ui/icons';
import { deepMergeForSave } from './TestDetailMergeUtils';
import { isTestDisabled } from './TestHelper';
import ConfirmDataTableActionButton from '../../../form/ConfirmDataTableActionButton';
import { COPY, DELETE, DISABLE, ENABLE, VERIFY } from '../../../../services/FormDataTableActionService';
import { DETAIL_NO_NEED_ALERT } from '../../infrastructure/InfrastructureConstants';
import { MenusItemsArrayButton } from '../../infrastructure/MenusItemsArrayButton';
import { useTestDetailSaveActions } from './hooks/UseTestDetailSaveActions';
import { useTestValidation } from './hooks/UseTestValidation';
import { useTestTypeContext } from './hooks/UseTestTypeContext';
import { MenuActionItem } from '../../../page-framework';

const BASE_URL = '/ngp/v2/service-tests/';

/**
 * Renders action buttons for the test detail toolbar.
 * Buttons: DELETE, ENABLE, DISABLE, VERIFY (conditional), COPY (edit mode only), Save menu
 *
 * handleCopyAction is defined in TestDetailPanel and passed via actionProps.
 */
const TestDetailActionButtons = ({
    groupExist,
    isActioning,
    formData,
    handleConfirmedDataTableAction,
    handleSubmit,
    handleSubmitFunction,
    saveDisabled,
    handleCopyAction, // ← defined in TestDetailPanel, passed via actionProps
    createSubmitTransform,
    submitTransform,
    setError,
    onVerify,
    verifyLoading,
    bttStateRef,
}) => {
    const disabled = isTestDisabled(formData);
    const isNewRecord = isEmpty(formData?._id);
    const { typeName: currentTypeName } = useTestTypeContext();

    // Validation and submit handlers are intentionally parallel with TestDetailBottomButtons.
    // Both components independently call useTestValidation and define their own handlers.
    // Merging would couple the toolbar and bottom button regions without proportionate benefit (~20 lines saved).
    const { handleSaveAndNewFn, handleSaveAndCopyFn } = useTestDetailSaveActions({
        baseUrl: BASE_URL,
        formData,
        submitTransform,
        createSubmitTransform,
        setError,
        currentTypeName,
    });

    const validateTest = useTestValidation(groupExist, formData, bttStateRef);

    const handleVerifyAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();
            if (onVerify) {
                // Merge formData with validated data (to mirror save behavior) and apply submitTransform
                handleSubmit(
                    validateTest((validatedData) => {
                        const merged = deepMergeForSave(formData, validatedData);
                        const afterSubmitTransform = submitTransform ? submitTransform(merged) : merged;
                        return onVerify(afterSubmitTransform);
                    })
                )();
            }
        },
        [handleSubmit, validateTest, onVerify, formData, submitTransform]
    );

    const handleSaveAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();
            handleSubmit(validateTest(handleSubmitFunction))();
        },
        [handleSubmit, handleSubmitFunction, validateTest]
    );

    const handleSaveAndNewAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();
            handleSubmit(validateTest(handleSaveAndNewFn))();
        },
        [handleSubmit, handleSaveAndNewFn, validateTest]
    );

    const handleSaveAndCopyAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();
            handleSubmit(validateTest(handleSaveAndCopyFn))();
        },
        [handleSubmit, handleSaveAndCopyFn, validateTest]
    );

    return (
        <>
            {!isNewRecord && (
                <>
                    <ConfirmDataTableActionButton
                        key={DELETE}
                        label={DELETE}
                        disabled={isActioning || verifyLoading}
                        handleConfirmedDataTableAction={(event) => handleConfirmedDataTableAction(event, DELETE, true)}
                        plural={false}
                        needConfirm
                    />
                    <ConfirmDataTableActionButton
                        key={ENABLE}
                        label={ENABLE}
                        disabled={isActioning || verifyLoading || !disabled}
                        handleConfirmedDataTableAction={(event) =>
                            handleConfirmedDataTableAction(event, ENABLE, DETAIL_NO_NEED_ALERT)
                        }
                        plural={false}
                        needConfirm={false}
                    />
                    <ConfirmDataTableActionButton
                        key={DISABLE}
                        label={DISABLE}
                        disabled={isActioning || verifyLoading || disabled}
                        handleConfirmedDataTableAction={(event) =>
                            handleConfirmedDataTableAction(event, DISABLE, DETAIL_NO_NEED_ALERT)
                        }
                        plural={false}
                        needConfirm={false}
                    />
                </>
            )}
            {groupExist && (
                <ConfirmDataTableActionButton
                    key={VERIFY}
                    label={VERIFY}
                    disabled={isActioning || verifyLoading}
                    handleConfirmedDataTableAction={handleVerifyAction}
                    needConfirm={false}
                />
            )}
            {!isNewRecord && (
                <ConfirmDataTableActionButton
                    key={COPY}
                    label={COPY}
                    disabled={isActioning || verifyLoading}
                    handleConfirmedDataTableAction={handleCopyAction}
                    needConfirm={false}
                />
            )}
            <MenusItemsArrayButton key="Save" label={<Trans>Save</Trans>} disabled={saveDisabled || verifyLoading}>
                {(closeMenu) => [
                    <MenuActionItem
                        icon={<Save />}
                        key={t`Save`}
                        label={t`Save`}
                        onClick={(event) => {
                            closeMenu();
                            handleSaveAction(event);
                        }}
                    />,
                    <MenuActionItem
                        icon={<AddCircleOutline />}
                        key={t`Save and New`}
                        label={t`Save and New`}
                        onClick={(event) => {
                            closeMenu();
                            handleSaveAndNewAction(event);
                        }}
                    />,
                    <MenuActionItem
                        icon={<FileCopyOutlined />}
                        key={t`Save and Copy`}
                        label={t`Save and Copy`}
                        onClick={(event) => {
                            closeMenu();
                            handleSaveAndCopyAction(event);
                        }}
                    />,
                ]}
            </MenusItemsArrayButton>
        </>
    );
};

TestDetailActionButtons.propTypes = {
    groupExist: PropTypes.bool.isRequired,
    isActioning: PropTypes.bool.isRequired,
    formData: PropTypes.object,
    handleConfirmedDataTableAction: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleSubmitFunction: PropTypes.func.isRequired,
    saveDisabled: PropTypes.bool.isRequired,
    handleCopyAction: PropTypes.func.isRequired,
    createSubmitTransform: PropTypes.func,
    submitTransform: PropTypes.func,
    setError: PropTypes.func.isRequired,
    onVerify: PropTypes.func,
    verifyLoading: PropTypes.bool.isRequired,
    bttStateRef: PropTypes.shape({ current: PropTypes.any }),
};

TestDetailActionButtons.defaultProps = {
    formData: undefined,
    createSubmitTransform: undefined,
    submitTransform: undefined,
    onVerify: undefined,
    bttStateRef: undefined,
};

export default TestDetailActionButtons;
